import React, { memo, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import ReactTooltip from 'react-tooltip';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import Can from '../../RBAC/Can';
import AssetService from '../../Services/AssetService';

import { REMOTE, REMOTE_EXECUTE } from '../../Constants/RBAC';
import IconsContext from '../../Context/IconsContext';

export default memo(CommandsWidget);

const initState = {
  error: '',
  executeRequestPending: false,
  jobParameter: '',
  showConfirmationModal: '',
};

function CommandsWidget(props) {
  const [state, updateState] = useState(initState);

  const source = CancelToken.source();

  const {
    _id: assetID,
    gtemplate,
    orgID,
    remote: assetRemote,
  } = props.asset || {};

  const commands =
    props.commands?.filter?.(({ commandID } = {}) => {
      return gtemplate?.commands?.some?.(({ _id }) => _id === commandID);
    }) || [];

  const commandToExecute = useMemo(() => {
    if (!state.showConfirmationModal) return {};
    return gtemplate?.commands?.find?.(
      ({ _id }) => _id === state.showConfirmationModal
    );
  }, [gtemplate?.commands, state.showConfirmationModal]);

  const executeCommand = async () => {
    try {
      if (!commandToExecute?._id) throw new Error('Invalid command selected');
      updateState((prevState) => ({
        ...prevState,
        executeRequestPending: true,
      }));

      const body = state.jobParameter
        ? { jobParameters: [state.jobParameter] }
        : {};

      await new AssetService().executeAssetCommand(
        assetID,
        { commandID: commandToExecute._id },
        body,
        source.token
      );
      props.handleCommandExecuted(commandToExecute?.name);
      updateState(initState);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        executeRequestPending: false,
      }));
    }
  };

  const handleParameterInput = ({ target: { name, value } }) => {
    updateState((prevState) => ({ ...prevState, [name]: value, error: '' }));
  };

  const toggleConfirmationModal = (showConfirmationModal = '') => {
    updateState((prevState) => ({ ...prevState, showConfirmationModal }));
  };

  return (
    <div className="p-3 px-md-5">
      {commands.map((c) => (
        <div className="my-2" key={c.commandID}>
          <Can
            data={{ assetRemote }}
            no={(data) => (
              <div data-for={c.friendlyName} data-tip={data}>
                <ExecuteCommandButton {...c} isDisabled />
                <ReactTooltip id={c.friendlyName} />
              </div>
            )}
            orgID={orgID}
            perform={REMOTE_EXECUTE}
            resource={REMOTE}
            yes={() => (
              <ExecuteCommandButton
                {...c}
                toggleConfirmationModal={toggleConfirmationModal}
              />
            )}
          />
        </div>
      ))}
      {state.showConfirmationModal ? (
        <CommandConfirmationModal
          {...props}
          {...state}
          command={commandToExecute}
          executeCommand={executeCommand}
          handleParameterInput={handleParameterInput}
          toggleConfirmationModal={toggleConfirmationModal}
        />
      ) : null}
    </div>
  );
}

CommandsWidget.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplate: PropTypes.shape({
      commands: PropTypes.arrayOf(PropTypes.object),
    }),
    nickname: PropTypes.string,
    orgID: PropTypes.string,
    remote: PropTypes.bool,
  }),
  commands: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      commandID: PropTypes.string,
      friendlyName: PropTypes.string,
    })
  ),
  handleCommandExecuted: PropTypes.func,
};

function ExecuteCommandButton(props) {
  const conditionallyDisabled = props.isDisabled ? { disabled: true } : {};
  return (
    <button
      {...conditionallyDisabled}
      className={`btn btn-block btn-${props.color} text-white shadow-none rounded-lg`}
      onClick={() => props.toggleConfirmationModal(props.commandID)}
    >
      {props.friendlyName}
    </button>
  );
}
ExecuteCommandButton.propTypes = {
  color: PropTypes.string,
  commandID: PropTypes.string,
  friendlyName: PropTypes.string,
  isDisabled: PropTypes.bool,
  toggleConfirmationModal: PropTypes.func,
};

function CommandConfirmationModal(props) {
  const { commandControlIcon } = useContext(IconsContext);
  const isParameterized = props.command?.parameters?.length;
  return (
    <Modal
      backdrop={props.executeRequestPending ? 'static' : true}
      centered
      isOpen={!!props.showConfirmationModal}
      toggle={() =>
        props.executeRequestPending ? null : props.toggleConfirmationModal()
      }
    >
      <ModalHeader
        className="border-bottom-0"
        toggle={() =>
          props.executeRequestPending ? null : props.toggleConfirmationModal()
        }
      >
        <img
          alt="command-icon"
          height={40}
          src={commandControlIcon}
          width={40}
        />
        <p className="mb-0 mx-2 text-dark font-weight-bold">Execute Command</p>
        <p className="mb-0 text-text-light-gray font-weight-light">
          {props.asset?.nickname ?? ''}
        </p>
      </ModalHeader>
      <ModalBody>
        <p>
          Please ensure that this is the Command that you wish to send to the
          device.
        </p>
        <div className="text-dark font-weight-bold d-flex align-items-center justify-content-between">
          <p>{props.command?.name}</p>
        </div>
        <div
          className={
            isParameterized ? 'form-group row align-items-center' : 'd-none'
          }
        >
          <label
            className="col-sm-4 col-form-label three-quarter-text"
            htmlFor="jobParameter"
          >
            Parameter:
          </label>
          <div className="col-sm-8">
            <input
              className="form-control form-control-sm"
              name="jobParameter"
              onChange={props.handleParameterInput}
              type="text"
              value={props.jobParameter}
            />
          </div>
        </div>
        <p className="text-alert-orange font-weight-bold">
          Warning: you can&apos;t undo this action.
        </p>
        <p className={props.error ? 'text-danger text-right' : 'd-none'}>
          {props.error}
        </p>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-sm btn-outline-secondary bg-white text-muted shadow-none rounded-lg"
          disabled={props.executeRequestPending}
          onClick={() => props.toggleConfirmationModal()}
        >
          Cancel
        </button>
        <button
          className="btn btn-sm modal-button-submit rounded-lg"
          disabled={
            props.executeRequestPending ||
            (isParameterized && !props.jobParameter)
          }
          onClick={props.executeCommand}
        >
          {props.executeRequestPending ? (
            <FontAwesomeIcon className="text-white" icon={faCircleNotch} spin />
          ) : (
            'Yes, Execute Command'
          )}
        </button>
      </ModalFooter>
      <style jsx global>{`
        .btn:focus {
          outline: none;
        }
        .modal-title {
          align-items: center;
          display: flex;
          flex-direction: column;
        }

        @media (min-width: 768px) {
          .modal-title {
            flex-direction: row;
          }
        }
      `}</style>
    </Modal>
  );
}
CommandConfirmationModal.propTypes = {
  asset: PropTypes.shape({ nickname: PropTypes.string }),
  command: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    command: PropTypes.string,
    parameters: PropTypes.arrayOf(PropTypes.object),
  }),
  error: PropTypes.string,
  executeCommand: PropTypes.func,
  executeRequestPending: PropTypes.bool,
  handleParameterInput: PropTypes.func,
  jobParameter: PropTypes.string,
  showConfirmationModal: PropTypes.string,
  toggleConfirmationModal: PropTypes.func,
};
